/**
 * inline_price_controller.js — the unified click-to-edit Price editor (Stimulus).
 *
 * Backs components/detail/inline_price.html on quote / job / invoice detail
 * pages. Merges what used to be two separate inline editors (flat amount +
 * line items) into ONE: the pencil opens a form with the amount field and an
 * expandable "Break down into line items" section driven by the shared
 * LineItemsManager (static/js/utils/line-items-manager.js) — the same engine the
 * edit modal uses. The amount input is bound to the manager, so adding items
 * auto-fills the amount and shows an offset when the user overrides it; saving
 * with no meaningful items posts use_line_items=false and the flat price stands.
 *
 * Save POSTs amount + line_items_json + use_line_items + show_line_items through
 * window.TurboEditModal. The update endpoint's sentinels preserve every other
 * field and re-render the amount partial (Turbo target #{entityType}-{id}-amount)
 * as a Turbo Stream replace, which swaps back to display mode with a fresh
 * controller and re-seeds from #{entityType}Data. Offline, it seeds from the
 * device's copy when that is newer (see _seed).
 *
 * Change-vs-quoted guard (jobs & invoices): when data-inline-price-quoted-amount
 * is present and the entered amount differs from it, Save first shows a confirm
 * modal (never native confirm() — blocked in Hotwire Native web views).
 *
 * Config (data-* on the controller root):
 *   data-inline-price-url            POST target (the entity's update endpoint)
 *   data-inline-price-entity-type    'quote' | 'job' | 'invoice'
 *   data-inline-price-quoted-amount  accepted-quote baseline (optional)
 *   data-currency-symbol / data-tax-name / data-tax-rate / data-vat-rates /
 *   data-tax-exempt                  LineItemsManager display config
 *
 * One live editor at a time: participates in the shared coordination protocol
 * (inline_editor_coordination.js). Dirty = a form-level 'input' listener
 * (LineItemsManager's programmatic auto-fill sets .value without events, so
 * only real typing counts); vetoes when saving would need a confirm modal
 * (quoted-amount / flat-override gates) or has no submittable amount.
 */

import { Controller } from "@hotwired/stimulus";
import { deviceLineItems } from "./_offline_line_items.js";
import { bindEditorGuards, releaseHeldUpdate, requestOpen } from "./inline_editor_coordination.js";

export default class extends Controller {
    static targets = ["breakdownToggle", "breakdownBody", "confirmModal", "confirmBody", "confirmTitle"];

    connect() {
        this.url = this.element.dataset.inlinePriceUrl;
        this.entityType = this.element.dataset.inlinePriceEntityType || 'item';
        this.prefix = 'inline' + this.entityType.charAt(0).toUpperCase() + this.entityType.slice(1);
        this.quotedAmount = this.element.dataset.inlinePriceQuotedAmount != null
            ? parseFloat(this.element.dataset.inlinePriceQuotedAmount)
            : null;
        this.display = this.element.querySelector('.inline-edit-display');
        this.form = this.element.querySelector('.inline-edit-form');
        this.amountInput = this.form ? this.form.querySelector('input[name="amount"]') : null;
        this.manager = null;
        this._amountTouched = false;

        // Track whether the user typed a flat amount directly this session — the
        // seeded amount can legitimately differ from the line-item total (a saved
        // adjustment), so the "replace items with flat price" confirm must key off
        // a deliberate edit, not the initial value. Manager auto-fill sets .value
        // without dispatching 'input', so it never sets this.
        if (this.amountInput) {
            this._onAmountInput = () => { this._amountTouched = true; };
            this.amountInput.addEventListener('input', this._onAmountInput);
        }

        // Form-level dirty flag: input events bubble from the amount field,
        // line-item rows and checkboxes alike, while programmatic .value
        // writes (manager auto-fill, seeding) fire none. Reset on every
        // _openEditor().
        this._formTouched = false;
        if (this.form) {
            this._onFormInput = () => { this._formTouched = true; };
            this.form.addEventListener('input', this._onFormInput);
        }

        this._unbindGuards = bindEditorGuards(this.element, {
            isOpen: () => !!this.form && !this.form.classList.contains('d-none'),
            isDirty: () => this._formTouched,
            // A confirm-needing save cannot happen silently, so it vetoes.
            canCommit: () => this._canCommit() && !this._wouldConfirm(),
            commit: () => this.save(),
            close: () => this._showDisplay(),
            veto: () => {
                this._focusAmount();
                if (window.TurboEditModal) {
                    window.TurboEditModal.showNotification('Save or cancel the price first.', 'error');
                }
            },
        });
    }

    disconnect() {
        if (this._unbindGuards) {
            this._unbindGuards();
            this._unbindGuards = null;
        }
        if (this.form && this._onFormInput) {
            this.form.removeEventListener('input', this._onFormInput);
            this._onFormInput = null;
        }
        if (this.amountInput && this._onAmountInput) {
            this.amountInput.removeEventListener('input', this._onAmountInput);
            this._onAmountInput = null;
        }
        this._hideConfirm();
    }

    edit() {
        this._openEditor();
    }

    // Display-state shortcut for an entity priced with no breakdown: open the
    // editor with the line-items section already expanded, so itemizing is one
    // tap instead of edit-then-expand.
    editBreakdown() {
        this._openEditor({ breakdown: true });
    }

    // Reveal the breakdown editor and seed one blank row so the user can type.
    toggleBreakdown() {
        if (!this._mountManager()) return;
        this._focusRow(this._openBreakdown(true));
    }

    addItem() {
        if (this.manager) this._focusRow(this.manager.add());
    }

    // Manual toggle of the "show to customer" box must sync the hidden input the
    // form submits from (the manager auto-checks it when items carry data).
    syncShowCheck(event) {
        const hidden = document.getElementById(`${this.prefix}ShowLineItems`);
        if (hidden) hidden.value = event.target.checked ? 'true' : 'false';
    }

    cancel() {
        // Editor state is disposable — the next edit re-seeds from the entity
        // data div, which only changes on a successful save. The reset puts the
        // saved total back in the Price field, which load() expects to find.
        this.form?.reset();
        this._showDisplay();
        releaseHeldUpdate(this.element);
    }

    async save(event) {
        if (event) event.preventDefault();
        // Mount lazily rather than requiring a prior edit(): an open-by-default
        // form can reach save without the editor ever having been opened.
        if (!this._mountManager() || !window.TurboEditModal) return;

        const hasItems = this.manager.hasMeaningfulItems();
        const useInput = document.getElementById(`${this.prefix}UseLineItems`);
        const entered = this._enteredTotal();

        // Flat-price override of an itemized entity: the user typed a flat amount
        // that differs from the line-item total, so they want a flat price that
        // REPLACES the breakdown. Confirm before discarding the items (confirmSave
        // clears them).
        if (this._flatOverridesItems(entered)) {
            this._pendingClearItems = true;
            this._showConfirm(
                'Replace line items with a flat price?',
                `Saving ${this._fmt(entered)} as a flat price will remove the itemized breakdown. Continue?`
            );
            return;
        }

        // use_line_items mirrors whether any row carries real data at save time:
        // all-blank (or none) submits 'false', leaving the flat amount as the price.
        this._pendingClearItems = false;
        if (useInput) useInput.value = hasItems ? 'true' : 'false';

        // Change-vs-quoted gate (jobs/invoices): confirm before saving a price
        // that differs from the accepted quote.
        if (this._differsFromQuote()) {
            const diff = entered - this.quotedAmount;
            const dir = diff > 0 ? 'above' : 'below';
            this._showConfirm(
                'Price differs from quote',
                `This sets the price to ${this._fmt(entered)} — ${this._fmt(diff)} ${dir} the quoted ${this._fmt(this.quotedAmount)}. Save this change?`
            );
            return;
        }
        await this._doSubmit();
    }

    // Confirm-modal "Save anyway" button.
    confirmSave() {
        this._hideConfirm();
        if (this._pendingClearItems) {
            // Discard the breakdown, keep the entered flat amount as the price.
            const set = (id, v) => { const el = document.getElementById(id); if (el) el.value = v; };
            set(`${this.prefix}LineItemsJson`, '[]');
            set(`${this.prefix}UseLineItems`, 'false');
            set(`${this.prefix}ShowLineItems`, 'false');
            this._pendingClearItems = false;
        }
        this._doSubmit();
    }

    // ---------------------------------------------------------------- privates

    // Swap display -> form. `breakdown` forces the line-items section open (the
    // display-state "Add line items" button).
    async _openEditor({ breakdown = false } = {}) {
        if (!this.form || !this._mountManager()) return false;
        if (!requestOpen(this.element)) return false;
        this._amountTouched = false;
        this._formTouched = false;
        await this._seed();
        let blankRow = null;
        if (breakdown || this.manager.hasItems()) {
            // Expanded, seeding a blank row only when there is nothing to show.
            blankRow = this._openBreakdown(!this.manager.hasItems());
        } else {
            // Flat price — collapsed, offering the "Add line items" button.
            this._closeBreakdown();
        }
        this.display.classList.add('d-none');
        this.form.classList.remove('d-none');
        // Focus AFTER the form is visible: focus() on a d-none element is a
        // silent no-op. Opening straight into an empty breakdown means the user
        // came to itemize, so the new row wins the cursor over the price. With
        // line items the total is theirs to compute: focusing (and selecting) it
        // invites typing over it, which replaces the breakdown, so nothing takes
        // focus and the user taps the row they came to change.
        if (blankRow) this._focusRow(blankRow);
        else if (!this.manager.hasItems()) this._focusAmount();
        return true;
    }

    // Put the cursor in a new row's Description field.
    _focusRow(row) {
        const field = row && row.querySelector('[data-line-item-field="description"]');
        if (field) field.focus();
    }

    _focusAmount() {
        if (!this.amountInput) return;
        this.amountInput.focus();
        // Select rather than plain focus: a programmatic focus drops the caret
        // at position 0, so typing prepends to the existing price. Editing a
        // price is almost always replacing it.
        this.amountInput.select();
    }

    async _doSubmit() {
        const result = await window.TurboEditModal.submit(this.form, {
            entityType: this.entityType,
            url: this.url,
            onError: (err) => {
                if (err && err.formError) {
                    window.TurboEditModal.showNotification(
                        'That price was not accepted. Please check it and try again.',
                        'error'
                    );
                }
            },
        });
        // Queued offline: no stream comes back to swap the editor out, so
        // close it here; the price's offline slot shows the pending value.
        if (result && result.queued) {
            this._showDisplay();
            releaseHeldUpdate(this.element);
        }
    }

    // A commit needs either a parseable flat amount or real line items (the
    // manager auto-fills the amount from items, so items imply an amount).
    _canCommit() {
        const entered = this.amountInput ? parseFloat(this.amountInput.value) : NaN;
        return !isNaN(entered) || !!(this.manager && this.manager.hasMeaningfulItems());
    }

    // True when save() would stop at one of its confirm gates instead of
    // submitting.
    _wouldConfirm() {
        return this._differsFromQuote() || this._flatOverridesItems(this._enteredTotal());
    }

    // The entered flat amount overrides an itemized breakdown: real items
    // exist and the typed amount differs from their total, so saving would
    // REPLACE the breakdown with the flat price. Gated on _amountTouched so a
    // no-op re-save of an item set with a stored adjustment doesn't
    // false-trigger.
    _flatOverridesItems(entered) {
        return !!(this.manager && this.manager.hasMeaningfulItems() &&
            this._amountTouched && !isNaN(entered) &&
            Math.abs(entered - (this.manager.calculatedTotal || 0)) > 0.005);
    }

    _differsFromQuote() {
        if (this.quotedAmount == null || !this.amountInput) return false;
        const entered = this._enteredTotal();
        if (isNaN(entered)) return false;
        return Math.abs(entered - this.quotedAmount) > 0.005;
    }

    // The total the Price field stands for, tax included: a flat price may be
    // typed tax-exclusive (LineItemsManager.amountTotal).
    _enteredTotal() {
        if (this.manager) return this.manager.amountTotal();
        return this.amountInput ? parseFloat(this.amountInput.value) : NaN;
    }

    _fmt(n) {
        const sym = this.element.dataset.currencySymbol || '$';
        return sym + Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
    }

    _showConfirm(title, body) {
        if (!this.hasConfirmModalTarget || typeof bootstrap === 'undefined') {
            // No shell / no Bootstrap — fail open rather than trap the edit.
            // Route through confirmSave so a pending item-discard still applies.
            this.confirmSave();
            return;
        }
        if (this.hasConfirmTitleTarget && title) this.confirmTitleTarget.textContent = title;
        if (this.hasConfirmBodyTarget) this.confirmBodyTarget.textContent = body;
        this._confirmInstance = bootstrap.Modal.getInstance(this.confirmModalTarget)
            || new bootstrap.Modal(this.confirmModalTarget);
        this._confirmInstance.show();
    }

    _hideConfirm() {
        if (this._confirmInstance) {
            this._confirmInstance.hide();
            this._confirmInstance = null;
        }
    }

    // Returns the seeded blank row (or null) so the caller can focus it.
    _openBreakdown(addBlankRow) {
        if (this.hasBreakdownBodyTarget) this.breakdownBodyTarget.classList.remove('d-none');
        if (this.hasBreakdownToggleTarget) this.breakdownToggleTarget.classList.add('d-none');
        if (addBlankRow && this.manager && !this.manager.hasItems()) {
            return this.manager.add(null, { highlight: false });
        }
        return null;
    }

    _closeBreakdown() {
        if (this.hasBreakdownBodyTarget) this.breakdownBodyTarget.classList.add('d-none');
        if (this.hasBreakdownToggleTarget) this.breakdownToggleTarget.classList.remove('d-none');
    }

    _mountManager() {
        if (this.manager) return this.manager;
        if (typeof window.LineItemsManager === 'undefined') {
            console.warn('[InlinePrice] LineItemsManager not loaded');
            return null;
        }
        const ds = this.element.dataset;
        const p = this.prefix;
        this.manager = new window.LineItemsManager({
            containerId: `${p}LineItemsContainer`,
            totalsId: `${p}LineItemsTotals`,
            rowIdPrefix: `${p}LineItem_`,
            globalFunctionPrefix: p,
            currencySymbol: ds.currencySymbol ?? '$',
            taxName: ds.taxName ?? 'GST',
            defaultTaxRate: ds.taxRate ?? 10,
            vatRates: ds.vatRates,
            taxExempt: ds.taxExempt,
            amountsAre: ds.amountsAre,
            photoOwner: () => ({ type: ds.inlinePriceEntityType, id: ds.inlinePriceEntityId }),
            elements: {
                subtotal: `${p}LineItemsSubtotal`,
                total: `${p}LineItemsTotal`,
                amount: `${p}Amount`,
                lineItemsJson: `${p}LineItemsJson`,
                useLineItems: `${p}UseLineItems`,
                showLineItems: `${p}ShowLineItems`,
                showLineItemsCheck: `${p}ShowLineItemsCheck`,
                amountLockedIndicator: `${p}AmountLockedIndicator`,
                amountOffsetText: `${p}AmountOffsetText`,
            },
            defaultQuantity: '',
        });
        return this.manager;
    }

    // Offline, the device's copy may be newer than the page: the user's own
    // waiting edit, or the record as last synced (_offline_line_items.js). The
    // form then names the version it was seeded from, so the queued edit is
    // checked against what the user actually edited.
    async _seed() {
        const ds = this.element.dataset;
        const device = ds.offlineIntentOperationValue
            ? await deviceLineItems({
                entityType: this.entityType,
                id: ds.offlineIntentTargetValue,
                operation: ds.offlineIntentOperationValue,
            })
            : null;
        if (device) {
            this.form.dataset.offlineRecordVersion = device.version;
            this.form.dataset.offlineRecordFields = device.fields;
            if (this.amountInput && device.amount != null) this.amountInput.value = String(device.amount);
            this.manager.load(device.items, device.show);
            return;
        }
        delete this.form.dataset.offlineRecordVersion;
        delete this.form.dataset.offlineRecordFields;
        const seedEl = document.getElementById(`${this.entityType}Data`);
        let items = [];
        let show = false;
        if (seedEl) {
            try {
                items = JSON.parse(seedEl.dataset.lineItems || '[]');
            } catch (_e) {
                items = [];
            }
            show = seedEl.dataset.showLineItems === 'true';
        }
        this.manager.load(items, show);
    }

    _showDisplay() {
        if (this.form) this.form.classList.add('d-none');
        if (this.display) this.display.classList.remove('d-none');
    }
}
